/**
 * There is no create-session endpoint: the backend mints a chat session on its first message and returns
 * the id on the SSE `complete` event. A chat whose first answer is still streaming therefore has no real
 * id yet, so it is given a client-side one with this prefix. That keeps it addressable — it can be listed
 * in the session list and re-opened from there — and every call that would hit the backend with an id
 * checks for the prefix first.
 */
export const TEMP_SESSION_ID_PREFIX = 'temp-';

export function buildTempSessionId(clientId: string): string {
  return TEMP_SESSION_ID_PREFIX + clientId;
}

export function isTempSessionId(sessionId: string | null | undefined): boolean {
  return !!sessionId && sessionId.startsWith(TEMP_SESSION_ID_PREFIX);
}

/**
 * Applies a backend session list without dropping chats that exist only on this client.
 *
 * A chat whose first answer is still streaming has no backend row yet, so a response that does not
 * mention it would otherwise remove the only handle on that stream — and the surfaces reload their
 * lists on events the user does not control, such as reopening the widget.
 */
export function keepInFlightChats<T extends { id: string }>(local: T[], fromBackend: T[]): T[] {
  const known = new Set(fromBackend.map(session => session.id));
  const inFlight = local.filter(session => isTempSessionId(session.id) && !known.has(session.id));
  return inFlight.length > 0 ? [...inFlight, ...fromBackend] : fromBackend;
}
